import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/kukusiki";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const kukusiki: CaseStudy = {
  slug: "kukusiki",
  title: "Кукусики",
  cardTitle: "Разработка сайта производителя продуктов питания «Кукусики»",
  subtitle: "Производитель семечек, арахиса, печенья и мармелада",
  industry: { label: "Еда и продукты", slug: "food" },
  eyebrow: "Корпоративный сайт · FMCG и пищевое производство",
  h1: "Разработка сайта производителя продуктов «Кукусики»",
  lead:
    "Разработали корпоративный сайт FMCG-бренда «Кукусики»: каталог продукции по категориям, раздел производства и отдельный путь для бизнеса и дистрибьюторов.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт производителя" },

  seo: {
    primaryKeyword: "разработка сайта производителя продуктов питания",
    secondaryKeywords: [
      "корпоративный сайт пищевого производителя",
      "сайт FMCG бренда",
      "сайт производителя снеков",
      "разработка сайта для дистрибьюторов",
    ],
    semanticEntities: [
      "каталог продукции",
      "категории снеков",
      "производство",
      "бизнесу и партнёрам",
      "дистрибьюторы",
      "регионы присутствия",
    ],
    excludedKeywords: [
      "купить семечки",
      "арахис купить",
      "мармелад купить",
      "печенье цена",
    ],
    title: "Сайт производителя «Кукусики» — кейс HAY.agency",
    description:
      "Кейс HAY.agency: корпоративный сайт FMCG-бренда «Кукусики» с каталогом продукции, производством и отдельным разделом для бизнеса и дистрибьюторов.",
    h1: "Разработка сайта производителя продуктов «Кукусики»",
    slug: "kukusiki",
    canonicalUrl: `${SITE_URL}/cases/kukusiki`,
    ogTitle: "Сайт производителя продуктов питания «Кукусики»",
    ogDescription:
      "Каталог продукции по категориям, раздел производства и путь для бизнеса и дистрибьюторов.",
    ogImage: `${SITE_URL}${dir}/kukusiki-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Структура каталога, производство и B2B-раздел",
    },
    relatedIndustry: {
      label: "Сайты для производителей и дистрибьюторов",
      to: routes.corporate,
      note: "Каталог продукции и отдельный путь для партнёров",
    },
    relatedCases: [
      {
        label: "E-commerce платформа для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Мультибрендовый каталог для производителя",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт производителя с каталогом и B2B-разделом?",
    text: "Спроектируем каталог продукции, раздел производства и путь для партнёров.",
    button: "Обсудить сайт производителя",
  },

  client: {
    name: "Кукусики",
    url: "https://kukusiki.ru/",
    city: "Россия",
    industry: "FMCG и пищевое производство",
  },
  year: "С 2006 года",
  services: [
    "Дизайн корпоративного сайта",
    "Разработка каталога продукции",
    "Раздел производства",
    "Раздел «Бизнесу и партнёрам»",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Кукусики" },
    { label: "Отрасль", value: "FMCG и пищевое производство" },
    { label: "Тип проекта", value: "Корпоративный сайт с каталогом и B2B-разделом" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог продукции с разделением на семечки, арахис, печенье и мармелад",
      "Отдельный раздел «Бизнесу и партнёрам» для дистрибьюторов",
      "Страница производства компании",
      "Блок с фактами о компании: с 2006 года, свыше 40 видов продукции, 3 производственных комплекса",
      "Присутствие бренда в 10 регионах РФ отражено на сайте",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "«Кукусики» — производитель продуктов питания с широким ассортиментом: семечки, арахис, печенье и мармелад. Компании нужен был сайт, который одновременно представляет бренд конечному покупателю и работает как инструмент для бизнеса и дистрибьюторов.",
        "Вторая часть задачи — показать масштаб производства: несколько производственных комплексов, десятки видов продукции и присутствие в регионах России.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: яркий продуктовый бренд без визуального шума",
    paragraphs: [
      "Интерфейс построен на ярких продуктовых цветах категорий — семечки, арахис, печенье и мармелад визуально разделены, но остаются частью единой системы бренда.",
      "Каталог и карточки продукции читаются с одного взгляда, а раздел для бизнеса выведен в отдельный, самостоятельный сценарий.",
    ],
    bullets: [
      "Яркая продуктовая палитра по категориям",
      "Компактные карточки продукции без перегрузки",
      "Отдельный путь для дистрибьюторов и партнёров",
    ],
  },

  identity: {
    fonts: [
      { name: "Fira Sans", role: "Заголовки", sample: "Кукусики" },
      { name: "Circe", role: "Текст и карточки", sample: "Каталог продукции" },
    ],
    colors: [
      { hex: "#FFF6E5", name: "Основной фон" },
      { hex: "#E8562C", name: "Акцент бренда" },
      { hex: "#232323", name: "Текст" },
    ],
  },

  cover: img(
    "kukusiki-case-cover",
    "Главная страница сайта производителя продуктов питания «Кукусики»",
    6,
  ),

  gallery: [
    img("kukusiki-services-catalog", "Каталог продукции «Кукусики» по категориям", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия сохраняет ту же логику: категории продукции, карточка и переход в раздел для бизнеса доступны в один экран.",
      "Смысловые блоки на мобильном идут горизонтальным скроллом, чтобы не терять крупные карточки категорий.",
    ],
    images: [
      mobile("kukusiki-mobile-home", "Мобильная главная сайта «Кукусики»"),
      mobile("kukusiki-mobile-catalog", "Мобильный каталог продукции «Кукусики»"),
    ],
  },

  columnBlocks: [
    {
      id: "brand",
      title: "Продуктовый бренд",
      paragraphs: [
        "Каталог разделён на четыре категории продукции — семечки, арахис, печенье и мармелад. Каждая категория показана упаковками продукции без превращения раздела в рекламный каталог.",
      ],
      columns: [
        { title: "Снеки", items: ["Семечки", "Арахис"] },
        { title: "Кондитерка", items: ["Печенье", "Мармелад"] },
      ],
    },
    {
      id: "production",
      title: "Производство",
      paragraphs: [
        "Раздел производства показывает реальный масштаб компании: работа с 2006 года, свыше 40 видов продукции и 3 производственных комплекса.",
      ],
      bullets: [
        "Компания на рынке с 2006 года",
        "Свыше 40 видов продукции",
        "3 производственных комплекса",
      ],
    },
    {
      id: "b2b",
      title: "B2B для партнёров",
      paragraphs: [
        "Раздел «Бизнесу и партнёрам» выделен в отдельный путь: дистрибьютор попадает в свой сценарий, минуя витрину для конечного покупателя.",
      ],
      bullets: [
        "Отдельный вход для дистрибьюторов",
        "Присутствие бренда в 10 регионах РФ",
      ],
    },
    {
      id: "content",
      title: "Контент",
      paragraphs: [
        "Раздел новинок и блог сделаны компактно и поддерживают продажи и работу с партнёрами, не перегружая структуру сайта.",
      ],
      bullets: ["Новинки продукции", "Компактный блог"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
    ],
  },
};
